import { FONT_FAMILIES, googleFontUrl, HEADER_SHADOW_PRESETS, SHADOW_STRIP_PRESETS, shadowStripGradient } from './blockDefaults'
import { SOCIAL_BRAND_COLORS } from './socialIcons'
import { wrapConditional } from './emailConditions'
import { sesAttr, buildSocialTrackingPlan } from './trackingTags'
// PRIMARY_BG, not the --brand-primary CSS variable: these strings are serialised
// into exported email HTML, which is read outside the browser.
import { PRIMARY_BG } from '../../../../theme/brand'

/**
 * Base URL of the Event service that renders the live countdown GIF (/e/timer.gif).
 *
 * ET_018: this used to prefer REACT_APP_EVENT_TRACKER_URL, which produced a broken
 * image in every preview and sent email. The whole /e/* router — timer AND the
 * open-pixel — is served by the event-mgmt host, not the tracker host. Verified:
 *
 *   stgevent-mgmt   /e/timer.gif -> 200 image/gif (GIF89a, ~2.6 KB)
 *   stgevent-mgmt   /e/o.gif     -> 200 image/gif
 *   stgevent-tracker/e/timer.gif -> 404 text/html
 *   stgevent-tracker/e/o.gif     -> 404 text/html
 *
 * So EVENT_API_URL comes first. REACT_APP_TIMER_BASE_URL is an explicit override
 * for deployments that do serve /e/* from a dedicated host; the tracker URL stays
 * as a last resort so nothing regresses where it happens to be the right host.
 */
const TIMER_BASE = (
  process.env.REACT_APP_TIMER_BASE_URL
  || process.env.REACT_APP_EVENT_API_URL
  || process.env.REACT_APP_EVENT_TRACKER_URL
  || ''
).replace(/\/$/, '')

// Approximate rendered width per size preset (px) — the GIF has its own intrinsic
// pixel size; this drives the <img width> so it displays crisply, max-width:100%.
const COUNTDOWN_WIDTH = { sm: 340, md: 460, lg: 580 }

/** Escape text destined for an HTML text node. */
const escText = s => String(s == null ? '' : s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

/**
 * Inbox preview snippets are roughly this many characters. Anything shorter and
 * the client backfills the gap with whatever visible copy comes first in the
 * body — usually "View in browser" or an image alt.
 */
const PREHEADER_SNIPPET_LEN = 140

/**
 * Hidden preheader div, injected as the FIRST child of <body>.
 *
 * Two parts matter:
 *  1. the hiding declarations — `display:none` alone is not enough, several
 *     clients ignore it, hence the 1px/0-opacity/0-max-size belt and braces and
 *     the text colour matched to the background.
 *  2. the `&nbsp;&zwnj;` run — this is NOT padding for looks. It pushes the
 *     snippet past the preheader text so the client stops pulling body copy in
 *     after it. zwnj is zero-width and nbsp is non-collapsing, so the pair stays
 *     invisible while still consuming snippet characters.
 *
 * Shared with the AMP exporter, which supplies its own `hideWith` (AMP4EMAIL
 * forbids the style attribute, so it passes a class instead).
 */
export function preheaderHtml(preheaderText, { hideWith } = {}) {
  const text = String(preheaderText || '').trim()
  if (!text) return ''
  const escaped = escText(text)
  const padPairs = Math.max(0, PREHEADER_SNIPPET_LEN - text.length)
  const pad = '&nbsp;&zwnj;'.repeat(padPairs)
  return `<div ${hideWith}>${escaped}${pad}</div>`
}

// Build the /e/timer.gif URL for a countdown block's props. Exported so the AMP
// exporter renders the exact same image. Target: props.targetDate (datetime-local
// ISO) → epoch seconds; empty → +7 days from now so a fresh block still counts down.
export function countdownImgUrl(p) {
  const units = [p.showDays && 'd', p.showHours && 'h', p.showMins && 'm', p.showSecs && 's'].filter(Boolean).join('') || 'dhms'
  const labelMap = { d: p.labelDays || 'Days', h: p.labelHours || 'Hours', m: p.labelMins || 'Mins', s: p.labelSecs || 'Secs' }
  const targetMs = p.targetDate ? new Date(p.targetDate).getTime() : (Date.now() + 7 * 86400 * 1000)
  const to = Math.floor((isNaN(targetMs) ? Date.now() + 7 * 86400 * 1000 : targetMs) / 1000)
  const params = new URLSearchParams({
    to: String(to),
    units,
    size: p.size || 'md',
    fg: (p.digitColor || '#111827').replace('#', ''),
    lc: (p.labelColor || '#6B7280').replace('#', ''),
    bg: (p.boxColor || '#ffffff').replace('#', ''),
    sc: (p.separatorColor || p.digitColor || '#111827').replace('#', ''),
    lcd: p.lcd === false ? '0' : '1',
  })
  if (p.showLabels) params.set('labels', units.split('').map(u => labelMap[u]).join(','))
  return `${TIMER_BASE}/e/timer.gif?${params.toString()}`
}

export function countdownImgWidth(p) {
  return COUNTDOWN_WIDTH[p.size] || COUNTDOWN_WIDTH.md
}

/**
 * Undo Quill's blanket space→&nbsp; conversion (ET_035 / ET_036).
 *
 * Quill 2's `getSemanticHTML()` runs `.replaceAll(' ', '&nbsp;')` over every text
 * leaf, so a plain sentence comes back as one unbreakable token:
 *   "Hi&nbsp;there&nbsp;friend"
 * Nothing downstream can wrap that. In the exported email `word-break:normal`
 * finds no break opportunity at all, so the text ran on one line and stretched the
 * email past contentWidth; on the canvas `overflow-wrap:break-word` fired as a last
 * resort and split words mid-syllable ("importa" / "nt").
 *
 * A run of N non-breaking spaces becomes N-1 of them plus one ordinary space: the
 * rendered width is identical (so deliberate multi-space runs survive) but every
 * run ends in a legal break opportunity. A single &nbsp; — the overwhelming
 * majority, one per word gap — becomes a plain space.
 *
 * The exact inverse of preserveSpaces() below; the two are deliberately adjacent.
 * Only text between tags is touched, never tag names or attribute values.
 * Idempotent: content with real spaces already passes through unchanged.
 *
 * Lives here rather than in quillEmailSetup so the export path does not have to
 * import Quill, which needs a DOM.
 */
const NBSP = String.fromCharCode(160)
const SPACE_RUN = new RegExp(`(?:&nbsp;|${NBSP}| )+`, 'g')
const ONE_SPACE = new RegExp(`&nbsp;|${NBSP}| `, 'g')

export function normalizeQuillHtml(html) {
  if (!html || typeof html !== 'string') return html
  return html.replace(/>([^<]*)</g, (_m, text) =>
    '>' + text.replace(SPACE_RUN, run => {
      const n = (run.match(ONE_SPACE) || []).length
      return '&nbsp;'.repeat(n - 1) + ' '
    }) + '<'
  )
}

// Quill stores multiple spaces as literal spaces, which HTML collapses to one on
// render. Preserve intentional runs of 2+ spaces by converting them to &nbsp;
// (renders everywhere incl. Outlook, unlike white-space:pre-wrap). Only touches
// text between tags — never tag names or attribute values. Single spaces are left
// alone so normal word-wrapping still works. Idempotent (won't touch existing &nbsp;).
export function preserveSpaces(html) {
  if (!html || typeof html !== 'string') return html
  // Keep runs of whitespace visible (HTML would collapse them) WITHOUT making the
  // run unbreakable. Replacing every space with &nbsp; welded words either side of
  // it into one long token, so a paragraph containing double spaces — which
  // contentEditable/TinyMCE produce readily — stopped wrapping at all (ET_033) or
  // wrapped mid-word instead (ET_032).
  // Fix: N-1 non-breaking spaces plus one ordinary space, which keeps the rendered
  // width identical but leaves a legal break opportunity at the end of the run.
  return html.replace(/>([^<]*)</g, (_m, text) =>
    '>' + text.replace(/ {2,}/g, s => '&nbsp;'.repeat(s.length - 1) + ' ') + '<'
  )
}

// Some backends return html_code with its `<`/`>` characters HTML-entity-escaped
// (e.g. for safe storage/display elsewhere). Decoding is a no-op on already-raw
// HTML, so it's safe to call unconditionally on any server-sourced html_code.
export function decodeHtmlEntities(str) {
  if (typeof str !== 'string' || !str) return str
  const textarea = document.createElement('textarea')
  textarea.innerHTML = str
  return textarea.value
}

// The backend escapes string fields generally, not just html_code — a saved block's
// props.content (e.g. "<p>Hello</p>") comes back as "&lt;p&gt;Hello&lt;/p&gt;", which then
// renders as literal visible tags instead of formatted content. Decode every block's
// content recursively (including nested column blocks) before loading them back into the canvas.
export function decodeBlocksContent(blocks) {
  if (!Array.isArray(blocks)) return blocks
  return blocks.map(block => {
    const props = { ...block.props }
    // normalizeQuillHtml also repairs emails SAVED BEFORE the Quill nbsp fix — their
    // stored content is still one unbreakable token per paragraph, so without this
    // they would keep failing to wrap even after the editor started emitting spaces.
    if (typeof props.content === 'string') props.content = normalizeQuillHtml(decodeHtmlEntities(props.content))
    if (block.type === 'columns' && Array.isArray(props.columnDefs)) {
      props.columnDefs = props.columnDefs.map(col => ({
        ...col,
        blocks: decodeBlocksContent(col.blocks || []),
      }))
    }
    return { ...block, props }
  })
}

function collectGoogleFontLinks(blocks, emailSettings) {
  const used = new Set()

  // Global font from emailSettings
  const globalEntry = FONT_FAMILIES.find(f => f.value === emailSettings?.fontFamily)
  if (globalEntry?.google) used.add(globalEntry.name)

  // Walk blocks for any per-block fontFamily
  function walk(blockList) {
    for (const b of blockList || []) {
      const ff = b.props?.fontFamily
      if (ff) {
        const entry = FONT_FAMILIES.find(f => f.value === ff)
        if (entry?.google) used.add(entry.name)
      }
      if (b.type === 'columns') {
        for (const col of b.props?.columnDefs || []) walk(col.blocks)
      }
    }
  }
  walk(blocks)

  return [...used].map(name =>
    `  <link rel="stylesheet" href="${googleFontUrl(name)}" />`
  ).join('\n')
}

export function blocksToHtml(blocks, emailSettings) {
  const {
    backgroundColor = '#F7F9FC',
    backgroundType = 'solid',
    contentBackground = '#ffffff',
    contentBackgroundType = 'solid',
    contentWidth = '600',
    fontFamily = 'Arial, Helvetica, sans-serif',
    linkColor = '#2563eb',
    preheaderText = '',
  } = emailSettings || {}

  // background-color only accepts solid colors — a gradient value needs the `background` shorthand instead,
  // otherwise the browser/email client silently drops the (invalid) declaration and no background renders at all.
  const bodyBgDecl = backgroundType === 'gradient' ? `background:${backgroundColor};` : `background-color:${backgroundColor};`
  const contentBgDecl = contentBackgroundType === 'gradient' ? `background:${contentBackground};` : `background-color:${contentBackground};`
  const preheaderHideColor = backgroundType === 'gradient' ? '#ffffff' : backgroundColor

  const preheader = preheaderHtml(preheaderText, {
    hideWith: `style="display:none;font-size:1px;color:${preheaderHideColor};line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;"`,
  })

  // Social Icons tracking ids are computed once per export (not stored) — see
  // trackingTags.js — since they're a pure function of platform + whether it
  // repeats elsewhere, unlike every other block type's frozen, persisted id.
  const socialPlan = buildSocialTrackingPlan(blocks)
  const bodyHtml = blocks.map(b => renderBlock(b, socialPlan)).filter(Boolean).join('\n')
  const googleFontLinks = collectGoogleFontLinks(blocks, emailSettings)

  return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>Email</title>
${googleFontLinks ? googleFontLinks + '\n' : ''}  <style type="text/css">
    body, #bodyTable { margin:0; padding:0; ${bodyBgDecl} }
    body { font-family:${fontFamily}; }
    table { border-spacing:0; }
    td { padding:0; }
    img { max-width:100%; border:0; display:block; }
    /* ET_034: this was text-decoration:none, so a link the author had underlined on
       the canvas arrived in the inbox as plain coloured text and was easy to miss.
       Underline is the default now; every element that must NOT look like body copy
       (button, social icon, story CTA, header nav, linked image) already sets
       text-decoration:none inline, and an inline style beats this stylesheet. */
    a { color:${linkColor}; text-decoration:underline; }
    /* Header bar mobile logo: ship both, show one. Clients cannot swap img src. */
    .hb-logo-mobile { display:none !important; }
    @media only screen and (max-width:620px) {
      .email-container { width:100% !important; }
      .col-block { display:block !important; width:100% !important; padding:4px 0 !important; }
      /* Header bar, mobileLayout = stack_all: split the logo/links CELLS (ET_030) */
      .hb-stack { display:block !important; width:100% !important; text-align:center !important; padding:4px 0 !important; }
      /* ...and stack the individual nav links. The layouts that put the logo on its
         own row have no second cell to split, so this rule is the only thing
         "Stack all" can mean for them — without it the setting was inert outside
         logo_inline, which is exactly what ET_030 reports. */
      .hb-stack-links a, .hb-stack-links img { display:block !important; margin-left:auto !important; margin-right:auto !important; }
      /* Separate mobile logo (ET_031) */
      .hb-logo-desktop { display:none !important; }
      .hb-logo-mobile { display:inline-block !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;${bodyBgDecl}">
  ${preheader}
  <table id="bodyTable" width="100%" cellpadding="0" cellspacing="0" border="0" style="${bodyBgDecl}">
    <tr>
      <td align="center" style="padding:20px 10px;">
        <table class="email-container" width="${contentWidth}" cellpadding="0" cellspacing="0" border="0" style="${contentBgDecl}max-width:${contentWidth}px;width:100%;">
          <tr>
            <td>
${bodyHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}

export function renderBlock(block, socialPlan) {
  if (!block || !block.props) return ''
  const p = block.props
  return wrapConditional(renderBlockInner(block, p, socialPlan), p._condition)
}

function renderBlockInner(block, p, socialPlan) {
  switch (block.type) {
    case 'headerBar': return renderHeaderBar(p)
    case 'story':    return renderStory(p)
    case 'ampForm':  return renderAmpForm(p)
    case 'countdown': return renderCountdown(p)
    case 'dropShadow': return renderDropShadow(p)
    case 'heading':  return renderHeading(p)
    case 'text':     return renderText(p)
    case 'image':    return renderImage(p)
    case 'button':   return renderButton(p)
    case 'video':    return renderVideo(p)
    case 'divider':  return renderDivider(p)
    case 'spacer':   return renderSpacer(p)
    case 'social':   return renderSocial(p, socialPlan?.get(block.id))
    case 'footer':   return renderFooter(p)
    case 'html':     return renderHtml(p)
    case 'columns':  return renderColumns(p, socialPlan)
    default:         return ''
  }
}

function bgStyle(p) {
  if (p.gradient) return `background:${p.gradient};`
  if (p.backgroundColor) return `background-color:${p.backgroundColor};`
  return ''
}

function pad(p) {
  return `padding:${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px;`
}

function fontStyle(p) {
  return p.fontFamily ? `font-family:${p.fontFamily};` : ''
}

/**
 * Absolute https endpoint that an AMP-form submission posts to.
 *
 * Shared with ampEmailExport (which needs it for `action-xhr`) so the AMP part and the
 * HTML part always post to the same place. It lives here rather than there because
 * ampEmailExport already imports from this module — the reverse would be circular.
 *
 * Must be absolute https: the amp4email validator rejects a relative action-xhr, and an
 * invalid AMP document makes the client discard the ENTIRE AMP part and fall back to
 * text/html. If the env gives nothing absolute, resolve against the current origin,
 * which is at least valid.
 */
export function formSubmitEndpoint() {
  const raw = (typeof process !== 'undefined' && process.env &&
    (process.env.REACT_APP_AMP_FORM_ENDPOINT
      || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/$/, '') + '/e/form-submit' : ''))) || '/e/form-submit'
  if (/^https:\/\//i.test(raw)) return raw
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || ''
  const resolved = origin
    ? origin.replace(/^http:\/\//i, 'https://').replace(/\/$/, '') + (raw.startsWith('/') ? raw : `/${raw}`)
    : raw
  if (typeof console !== 'undefined' && !/^https:\/\//i.test(resolved)) {
    // eslint-disable-next-line no-console
    console.warn(`[emailExport] Form endpoint is not absolute https ("${resolved}"). Gmail will reject the AMP part. Set REACT_APP_AMP_FORM_ENDPOINT.`)
  }
  return resolved
}

// ── AMP Form (HTML fallback — a real <form> so it submits wherever forms are
// supported; clients that strip <form> still show the fields + the reply prompt) ──
function renderAmpForm(p) {
  const accent = p.accentColor || PRIMARY_BG
  const radius = p.borderRadius || 12

  // REAL form controls, wrapped in a styled container.
  //
  // Static divs (the previous approach) meant the Preview showed the fields but you
  // could not type and the dropdown would not open (ET_025). The Preview is a browser
  // iframe, so real <input>/<select>/<textarea> work there — and the <select> lists
  // every configured option instead of just the first.
  //
  // The container carries the border/background and the control sits inside it with
  // no chrome of its own, so if a mail client strips the control (Gmail does) the
  // field is still visibly present rather than vanishing. Note that interaction in an
  // actual inbox is NOT possible from this part at all — that needs the AMP part
  // (text/x-amp-html) from ampEmailExport; this stays a static fallback by design.
  const boxStyle = `border:1px solid #d1d5db;border-radius:6px;background:#ffffff;font-size:13px;color:#9ca3af;padding:9px 11px;`
  const bare = `border:0;outline:none;width:100%;background:transparent;font-family:inherit;font-size:13px;color:#374151;padding:0;margin:0;`

  const fauxField = (f) => {
    const label = f.label
      ? `<div style="font-size:13px;font-weight:600;color:#374151;margin-bottom:5px;">${escText(f.label)}${f.required ? ` <span style="color:#ef4444;">*</span>` : ''}</div>`
      : ''
    const name = escText(f.name || f.id || 'field')
    // ET_046: `required` was shown as a red asterisk but never emitted on the control,
    // so nothing stopped an empty submit. Native validation now blocks it, which is what
    // "should allow submission when the required fields are completed" asks for.
    const req = f.required ? ' required' : ''
    let control
    if (f.type === 'textarea') {
      control = `<div style="${boxStyle}min-height:56px;"><textarea name="${name}" rows="3"${req} style="${bare}resize:vertical;min-height:52px;" placeholder="${escText(f.placeholder || '')}"></textarea></div>`
    } else if (f.type === 'select') {
      const opts = (f.options && f.options.length ? f.options : ['Option 1'])
      control = `<div style="${boxStyle}padding:7px 9px;"><select name="${name}"${req} style="${bare}cursor:pointer;">${
        opts.map(o => `<option value="${escText(o)}">${escText(o)}</option>`).join('')
      }</select></div>`
    } else if (f.type === 'rating') {
      // The radio must stay VISIBLE. Hiding it with display:none left nothing to
      // click and no selected state to see, so the rating looked unselectable — and
      // a hidden input is inert in mail clients regardless. Same shape as the AMP
      // part: a real radio beside the number, inside the label so either is a target.
      // Only the first radio carries `required` — for a radio group that is enough to
      // require a choice, and repeating it changes nothing.
      control = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>${
        [1, 2, 3, 4, 5].map(n => `<td style="padding-right:10px;"><label style="display:inline-block;font-size:13px;color:#374151;cursor:pointer;white-space:nowrap;"><input type="radio" name="${name}" value="${n}"${n === 1 ? req : ''} style="vertical-align:middle;margin:0 4px 0 0;" />${n}</label></td>`).join('')
      }</tr></table>`
    } else {
      const ph = f.placeholder || (f.type === 'email' ? 'name@example.com' : '')
      control = `<div style="${boxStyle}"><input type="${f.type === 'email' ? 'email' : 'text'}" name="${name}"${req} style="${bare}" placeholder="${escText(ph)}" /></div>`
    }
    return `<div style="margin:0 0 14px;">${label}${control}</div>`
  }

  const qs = (p.fields || []).map(fauxField).join('')

  // ET_046 (second pass): the fields were real controls but there was no <form> around
  // them and Submit was a plain <div>, so nothing happened on click — the reported
  // "form is interactive but submit button is still not working". A real
  // <form action=… method="post"> with <button type="submit"> posts wherever forms are
  // supported (the app's own Preview iframe, Apple Mail, a browser "view in browser"
  // copy). Clients that strip <form> — Gmail among them — are no worse off than before:
  // the fields still render and the note below still points at replying or the AMP part.
  //
  // `fallbackUrl` stays honoured as the target when the author has pointed at a hosted
  // copy of the form; otherwise posts to the same endpoint as the AMP part's action-xhr,
  // so one backend handler serves both.
  const fallbackUrl = String(p.fallbackUrl || '').trim()
  const action = fallbackUrl || formSubmitEndpoint()
  const submitLabel = escText(p.submitLabel || 'Submit')
  const submit = `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-top:2px;">
    <tr><td align="center" bgcolor="${accent}" style="border-radius:8px;">
      <button type="submit" style="display:inline-block;padding:11px 26px;font-size:14px;font-weight:700;color:#ffffff;background-color:${accent};border:0;border-radius:8px;font-family:inherit;cursor:pointer;">${submitLabel}</button>
    </td></tr></table>`

  // ET_028: was hardcoded #ffffff, which discarded the author's colour on export.
  const card = p.cardBackground || '#ffffff'
  const box = `<div style="border:1px solid ${accent}33;border-radius:${radius}px;padding:18px;background:${card};">
    ${p.title ? `<div style="font-size:18px;font-weight:800;color:#111827;margin-bottom:4px;">${escText(p.title)}</div>` : ''}
    ${p.intro ? `<div style="font-size:13px;color:#6B7280;margin-bottom:14px;">${escText(p.intro)}</div>` : ''}
    <form action="${escText(action)}" method="post" target="_blank" style="margin:0;">
    ${qs}
    ${submit}
    </form>
    <div style="margin-top:12px;font-size:12px;color:#6B7280;">Open this email in Gmail or Yahoo to respond inline, or simply reply to this email.</div>
  </div>`
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}">${box}</td></tr>
</table>`
}

// ── Story (HTML fallback = poster linking to the live story) ─────────────────
// This is the text/html part. The interactive amp-carousel version is produced
// separately by ampEmailExport.js (the text/x-amp-html part of the email).
function renderStory(p) {
  const align = p.alignment || 'center'
  const url = p.url || '#'
  const w = p.width || 100
  const radius = p.borderRadius || 0
  const ctaBg = p.ctaBackgroundColor || PRIMARY_BG
  const ctaColor = p.ctaTextColor || '#ffffff'

  // Poster + title, WITHOUT overlaying text on the image.
  //
  // Two overlay attempts failed in Gmail, for different reasons:
  //   1. position:absolute — Gmail strips `position`, so the spans dropped out of
  //      the image and rendered after it as detached fragments.
  //   2. background-image + background-size:cover — Gmail strips `background-size`,
  //      so the poster rendered at its NATURAL size inside the fixed cell and got
  //      cropped. Measured: a 390x693 poster in a 300x533 cell is 1.3x oversized,
  //      clipping 45px off each side and cutting the cover headline in half.
  //
  // So: a plain <img> (the `width` attribute scales reliably everywhere, and auto
  // height means ANY poster aspect is respected — no forced 9:16), with the title as
  // a caption bar flush beneath it. Sharing the wrapper's rounded corners makes it
  // read as one card. Nothing can detach, nothing can crop, no client-specific CSS.
  //
  // Trade-off, deliberate: the title sits just below the image rather than on top of
  // it. Slightly different from the designer, but correct in every client — which the
  // two overlay versions were not.
  const poster = p.poster
  const captionParts = []
  if (p.showPlayBadge) captionParts.push(`<span style="display:inline-block;color:#ffffff;font-size:12px;">&#9658;</span>`)
  if (p.showTitle && p.storyTitle) captionParts.push(`<span style="color:#ffffff;">${escText(p.storyTitle)}</span>`)

  const caption = captionParts.length
    ? `<tr><td style="padding:9px 12px;background-color:#1f2937;font-size:14px;font-weight:700;color:#ffffff;font-family:Arial,Helvetica,sans-serif;text-align:left;border-radius:0 0 ${radius}px ${radius}px;">
        ${captionParts.join('&nbsp;&nbsp;')}
      </td></tr>`
    : ''

  const img = poster
    ? `<table width="300" cellpadding="0" cellspacing="0" border="0" role="presentation" align="${align}" style="max-width:300px;width:${w}%;">
    <tr><td style="font-size:0;line-height:0;">
      <a href="${url}" target="_blank" style="display:block;text-decoration:none;"${sesAttr({ trackingId: p.thumbnailTrackingId, noTrack: p.thumbnailNoTrack })}>
        <img src="${poster}" alt="${escText(p.storyTitle || 'Story')}" width="300" style="display:block;width:100%;max-width:300px;height:auto;border:0;border-radius:${caption ? `${radius}px ${radius}px 0 0` : `${radius}px`};" />
      </a>
    </td></tr>
    ${caption}
  </table>`
    : ''

  // Table-based button: an inline-block <a> is what left the CTA misaligned in sent
  // mail (ET_021). A single-cell table with bgcolor is the Outlook-safe pattern.
  //
  // The table also needs the `align` ATTRIBUTE, not just `margin:auto`: Outlook
  // renders through Word, which ignores auto margins on tables, so the button hugged
  // the left edge there while looking centred everywhere else. align= is the one
  // horizontal-positioning hook Word honours. Margins stay as the modern-client path.
  const ctaMargin = align === 'left' ? 'margin:10px 0 0;' : align === 'right' ? 'margin:10px 0 0 auto;' : 'margin:10px auto 0;'
  const cta = p.showCta
    ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation" align="${align}" style="${ctaMargin}">
      <tr><td align="center" bgcolor="${ctaBg}" style="border-radius:8px;">
        <a href="${url}" target="_blank" style="display:inline-block;padding:11px 22px;font-size:14px;font-weight:700;color:${ctaColor};text-decoration:none;border-radius:8px;white-space:nowrap;"${sesAttr(p)}>&#9658;&nbsp;${escText(p.ctaLabel || 'View Story')}</a>
      </td></tr></table>`
    : ''

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}text-align:${align};">
    ${img}${cta}
  </td></tr>
</table>`
}

// ── Countdown timer (live animated GIF from the Event service) ───────────────
function renderCountdown(p) {
  const align = p.alignment || 'center'
  const w = countdownImgWidth(p)
  const src = countdownImgUrl(p)
  const alt = p.targetDate ? `Offer ends ${new Date(p.targetDate).toLocaleString()}` : 'Countdown'
  const img = `<img src="${src}" alt="${alt}" width="${w}" style="display:inline-block;border:0;max-width:100%;height:auto;" />`
  const wrapped = p.link ? `<a href="${p.link}" target="_blank"${sesAttr(p)}>${img}</a>` : img
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}text-align:${align};">${wrapped}</td></tr>
</table>`
}

// ── Drop shadow (decorative elevation strip) ─────────────────────────────────
function renderDropShadow(p) {
  const h = (SHADOW_STRIP_PRESETS[p.shadowColor] || SHADOW_STRIP_PRESETS.medium).height
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}">
    <div style="height:${h}px;line-height:${h}px;font-size:0;background:${shadowStripGradient(p.shadowColor)};">&nbsp;</div>
  </td></tr>
</table>`
}

// ── Header bar (branded logo + nav) ──────────────────────────────────────────
function headerLinkStyle(p) {
  const deco = []
  if (p.linkUnderline) deco.push('underline')
  if (p.linkStrike) deco.push('line-through')
  return [
    `font-family:${p.linkFontFamily || 'Arial, Helvetica, sans-serif'}`,
    `font-size:${p.linkFontSize || 16}px`,
    `color:${p.linkColor || '#1A1A1A'}`,
    `letter-spacing:${p.linkLetterSpacing || 0}px`,
    `font-weight:${p.linkBold ? 'bold' : 'normal'}`,
    `font-style:${p.linkItalic ? 'italic' : 'normal'}`,
    `text-decoration:${deco.length ? deco.join(' ') : 'none'}`,
    `padding:${p.linkPaddingV || 0}px ${p.linkPaddingH || 0}px`,
    'display:inline-block',
  ].join(';')
}

function headerLinksHtml(p) {
  return (Array.isArray(p.links) ? p.links : []).map(l => {
    if (l.kind === 'image' && l.src) {
      const img = `<img src="${l.src}" alt="${l.alt || ''}" style="display:inline-block;border:0;max-height:${(p.linkFontSize || 16) + 12}px;vertical-align:middle;padding:${p.linkPaddingV || 0}px ${p.linkPaddingH || 0}px;" />`
      return l.url ? `<a href="${l.url}" target="_blank" style="text-decoration:none;"${sesAttr(l)}>${img}</a>` : img
    }
    return `<a href="${l.url || '#'}" target="_blank" style="${headerLinkStyle(p)}"${sesAttr(l)}>${l.label || ''}</a>`
  }).join('')
}

function headerLogoHtml(p) {
  if (!p.logoSrc) return ''
  const w = p.logoMaxWidth ? `width:${p.logoMaxWidth}px;max-width:${p.logoMaxWidth}px;` : ''
  const link = (inner) => p.logoLink ? `<a href="${p.logoLink}" target="_blank"${sesAttr({ trackingId: p.logoTrackingId, noTrack: p.logoNoTrack })}>${inner}</a>` : inner
  const img = (src, cls) => `<img src="${src}" alt="${p.logoAlt || ''}"${cls ? ` class="${cls}"` : ''} style="display:inline-block;border:0;${w}" />`

  // ET_029: a separate mobile logo was configurable in the model but never emitted.
  // Email clients cannot swap an img src responsively, so ship both and toggle
  // visibility in the media query (the standard show/hide approach).
  const useSame = p.useSameLogoAsDesktop !== false
  if (useSame || !p.mobileLogoSrc) return link(img(p.logoSrc))
  return link(`${img(p.logoSrc, 'hb-logo-desktop')}${img(p.mobileLogoSrc, 'hb-logo-mobile')}`)
}

function renderHeaderBar(p) {
  const border = (p.borderStyle && p.borderStyle !== 'none' && p.borderWidth)
    ? `border:${p.borderWidth}px ${p.borderStyle} ${p.borderColor};` : ''
  const shadow = HEADER_SHADOW_PRESETS[p.dropShadow] ? `box-shadow:${HEADER_SHADOW_PRESETS[p.dropShadow]};` : ''
  const outer = `${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}${border}${shadow}border-radius:${p.cornerRadius || 0}px;`
  const valign = ({ top: 'top', middle: 'middle', bottom: 'bottom' })[p.linkVAlign] || 'middle'
  const logo = headerLogoHtml(p)
  const links = headerLinksHtml(p)
  const gap = p.logoLinkSpacing || 0

  // ET_030: `stack_all` has two separate jobs, and only the first was implemented —
  // which is why the setting appeared to work for logo_inline and nowhere else.
  //   1. split the logo/links CELLS onto their own lines (logo_inline only — it is
  //      the one layout with two cells in a row).
  //   2. stack the individual nav LINKS on top of each other (every layout).
  // `linksCls` carries job 2 and is applied to whichever cell holds the links.
  const stackAll = p.mobileLayout !== 'inline'
  const linksCls = stackAll ? ' class="hb-stack-links"' : ''

  let inner
  if (p.desktopLayout === 'links_only') {
    const align = p.linkHAlign === 'manual' ? p.linkAlignment : 'center'
    inner = `<tr><td${linksCls} align="${align}" style="text-align:${align};">${links}</td></tr>`
  } else if (p.desktopLayout === 'logo_stacked') {
    const linkAlign = p.linkHAlign === 'manual' ? p.linkAlignment : p.logoAlignment
    inner = `<tr><td align="${p.logoAlignment}" style="text-align:${p.logoAlignment};padding-bottom:${gap}px;">${logo}</td></tr>
      <tr><td${linksCls} align="${linkAlign}" style="text-align:${linkAlign};">${links}</td></tr>`
  } else if (p.desktopLayout === 'logo_centered') {
    // ET_032: this branch hardcoded center, so Manual Link Alignment was ignored
    // here (it only worked for links_only and logo_stacked). The logo stays centred;
    // only the links row follows the manual setting.
    const linkAlign = p.linkHAlign === 'manual' ? (p.linkAlignment || 'center') : 'center'
    inner = `<tr><td align="center" style="text-align:center;padding-bottom:${gap}px;">${logo}</td></tr>
      <tr><td${linksCls} align="${linkAlign}" style="text-align:${linkAlign};">${links}</td></tr>`
  } else {
    // logo_inline: logo (aligned per logoAlignment) left, links right on one row.
    const stackCls = stackAll ? ' class="hb-stack"' : ''
    // ET_032: links were hardcoded to the right here, ignoring Manual Link Alignment.
    const linkAlign = p.linkHAlign === 'manual' ? (p.linkAlignment || 'right') : 'right'
    // ET_032 (second pass): reading the manual value still rendered the same for
    // left/center/right. With no width on either cell, auto table layout sizes both
    // to their content, so `align` had almost no room to move the links within its
    // own cell. Splitting the row 50/50 gives each cell space of its own, which is
    // what makes the three options visibly different. Auto layout still lets a wide
    // logo overflow its half, so nothing clips.
    inner = `<tr>
      <td${stackCls} width="50%" align="${p.logoAlignment || 'left'}" valign="${valign}" style="width:50%;text-align:${p.logoAlignment || 'left'};">${logo}</td>
      <td${stackAll ? ' class="hb-stack hb-stack-links"' : ''} width="50%" align="${linkAlign}" valign="${valign}" style="width:50%;text-align:${linkAlign};">${links}</td>
    </tr>`
  }

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${outer}">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation"><tbody>${inner}</tbody></table>
  </td></tr>
</table>`
}

function renderHeading(p) {
  const tag = p.tag || 'h2'
  const body = preserveSpaces(`<${tag} style="margin:0;padding:0;font-size:${p.fontSize}px;font-weight:${p.fontWeight};color:${p.color};text-align:${p.alignment};line-height:1.3;${fontStyle(p)}">${p.content}</${tag}>`)
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${bgStyle(p)}">
    ${body}
  </td></tr>
</table>`
}

function renderText(p) {
  // ET_036: the previous comment here claimed overflow-wrap:break-word was enough to
  // stop a long unbroken run stretching the email. It is not, and this is the whole
  // bug: `overflow-wrap:break-word` permits a break when laying out, but it does NOT
  // reduce the box's MIN-CONTENT width. The wrapper below is an auto-layout table,
  // and an auto-layout table sizes itself to its content's min-content width — so the
  // table grew to fit the unbroken run and dragged .email-container past its
  // max-width, exactly the "single line, email gets wider" report.
  //
  // Two changes, both needed:
  //   1. table-layout:fixed + width:100% — the table now takes its width from the
  //      600px container instead of from its content, so it can never push wider.
  //   2. overflow-wrap:anywhere — unlike break-word this DOES lower min-content, so
  //      the run actually breaks rather than overflowing the now-fixed cell. Declared
  //      after break-word so clients that do not know `anywhere` keep the old value.
  //
  // Still no word-break:break-all — that splits ordinary words mid-word
  // ("importa"/"nt"). `anywhere` only breaks a run that cannot fit on its own line,
  // so normal prose is unaffected.
  const wrap = 'overflow-wrap:break-word;word-wrap:break-word;overflow-wrap:anywhere;word-break:normal;'
  const body = preserveSpaces(`<div style="font-size:${p.fontSize}px;font-weight:${p.fontWeight};color:${p.color};text-align:${p.alignment};line-height:${p.lineHeight};${wrap}${fontStyle(p)}">${p.content}</div>`)
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="table-layout:fixed;width:100%;">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}">
    ${body}
  </td></tr>
</table>`
}

function renderImage(p) {
  // `display:block` — NOT inline-block. An inline-block image sits on the text
  // baseline, so the line box reserves room for descenders and leaves a few px of
  // dead space under the image before the next block. The stylesheet already sets
  // img{display:block} but an inline style beats it, so it has to be set here.
  // display:block ignores the parent's text-align, hence auto margins for
  // alignment. line-height/font-size 0 on the cell kills any residual line box.
  const align = p.alignment || 'center'
  const margin = align === 'left' ? 'margin:0;'
    : align === 'right' ? 'margin:0 0 0 auto;'
    : 'margin:0 auto;'
  const imgTag = `<img src="${p.src || 'https://placehold.co/600x300'}" alt="${p.alt || ''}" style="max-width:${p.width}%;border-radius:${p.borderRadius}px;display:block;border:0;${margin}" />`
  const wrapped = p.link ? `<a href="${p.link}" target="_blank" style="display:block;${margin}text-decoration:none;"${sesAttr(p)}>${imgTag}</a>` : imgTag
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}text-align:${align};font-size:0;line-height:0;">${wrapped}</td></tr>
</table>`
}

function renderButton(p) {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}text-align:${p.alignment};">
    <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="display:inline-table;">
      <tr><td style="background-color:${p.backgroundColor};border-radius:${p.borderRadius}px;">
        <a href="${p.url || '#'}" target="_blank" style="display:inline-block;padding:${p.buttonPaddingV}px ${p.buttonPaddingH}px;font-size:${p.fontSize}px;font-weight:${p.fontWeight};color:${p.textColor};text-decoration:none;border-radius:${p.borderRadius}px;"${sesAttr(p)}>${p.label}</a>
      </td></tr>
    </table>
  </td></tr>
</table>`
}

// Extracts the video ID from any common YouTube URL shape (embed / watch / short link)
export function extractYoutubeId(url) {
  if (!url) return ''
  const patterns = [
    /embed\/([a-zA-Z0-9_-]+)/,
    /[?&]v=([a-zA-Z0-9_-]+)/,
    /youtu\.be\/([a-zA-Z0-9_-]+)/,
  ]
  for (const re of patterns) {
    const m = url.match(re)
    if (m) return m[1]
  }
  return ''
}

/**
 * ET_050: the play icon sat below the thumbnail, left-aligned, looking detached.
 *
 * Two things were wrong. The `<a>` carried `position:relative` for an overlay that was
 * never there, and the badge was a separate `<p>` AFTER the anchor — so it could only
 * ever render below the image. The `text-align:center` on that `<p>` also loses to
 * Gmail/Outlook resetting `<p>` margins and alignment, which is why it read as
 * left-aligned rather than merely below.
 *
 * A genuine overlay cannot be done with `position` — every Gmail client strips it (the
 * same finding is recorded on renderStory above). What DOES work is a fixed-size table
 * cell carrying the thumbnail as a CELL BACKGROUND with the badge as its content:
 * supported by Gmail, Apple Mail, Outlook.com and, via the `background` attribute, the
 * Word engine in desktop Outlook.
 *
 * The catch with cell backgrounds is that `background-size` is stripped in Gmail, so the
 * image must already be the size of the cell. YouTube's `hqdefault.jpg` is always
 * 480x360 with the 16:9 frame letterboxed inside it, so a 480x270 cell plus
 * `background-position:center` crops off exactly the two 45px bars and leaves the frame —
 * no scaling needed anywhere. `background-size:cover` is emitted as well, and for this
 * particular pair it computes to the identical crop, so clients that honour it and
 * clients that drop it agree. The mobile media query swaps in `mqdefault.jpg`, which is
 * a true 16:9 320x180 with no letterbox, so that pair needs no cropping either.
 *
 * A CUSTOM thumbnail has unknown dimensions, so the cell-background trick would crop or
 * zoom it unpredictably. Those keep a real `<img>` — which always scales correctly — with
 * the badge in a caption bar flush beneath it, sharing the image's rounded corners so it
 * reads as one card. Same trade-off, and the same shape, as renderStory's caption.
 */
function renderVideo(p) {
  // Always link to the normal watch page: opening an /embed/ URL directly (outside
  // an iframe) triggers YouTube's "Error 153" on many videos instead of just playing.
  const videoId = extractYoutubeId(p.videoUrl)
  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : (p.videoUrl || '#')
  const align = p.alignment || 'center'
  const cell = `${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}text-align:${align};`
  const custom = String(p.thumbnailUrl || '').trim()

  // Circular play badge. The solid #000000 is declared first as the Outlook fallback —
  // it drops rgba() and would otherwise render no fill at all; border-radius is also
  // unsupported there, so it degrades to a black square, which still reads as a badge.
  const badge = (size) => `<span style="display:inline-block;width:${size}px;height:${size}px;line-height:${size}px;border-radius:${size}px;background-color:#000000;background-color:rgba(0,0,0,0.65);color:#ffffff;font-size:${Math.round(size * 0.42)}px;text-align:center;vertical-align:middle;font-family:Arial,Helvetica,sans-serif;">&#9658;</span>`

  if (videoId && !custom) {
    const cls = `xnv${videoId.replace(/[^A-Za-z0-9]/g, '')}`
    const bgDesktop = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    const bgMobile = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${cell}">
    <style>
    @media only screen and (max-width:620px) {
      .${cls} { width:320px !important; height:180px !important; background-image:url(${bgMobile}) !important; }
      .${cls} a { width:320px !important; height:180px !important; line-height:180px !important; }
    }
    </style>
    <table cellpadding="0" cellspacing="0" border="0" role="presentation" align="${align}" style="display:inline-table;">
      <tr>
        <td class="${cls}" width="480" height="270" align="center" valign="middle" background="${bgDesktop}" style="width:480px;height:270px;padding:0;background-image:url(${bgDesktop});background-position:center center;background-repeat:no-repeat;background-size:cover;border-radius:8px;text-align:center;">
          <a href="${watchUrl}" target="_blank" style="display:block;width:480px;height:270px;line-height:270px;text-align:center;text-decoration:none;"${sesAttr({ trackingId: p.thumbnailTrackingId, noTrack: p.thumbnailNoTrack })}>${badge(58)}</a>
        </td>
      </tr>
    </table>
  </td></tr>
</table>`
  }

  const thumbSrc = custom || 'https://placehold.co/600x338'
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${cell}">
    <table width="460" cellpadding="0" cellspacing="0" border="0" role="presentation" align="${align}" style="display:inline-table;max-width:100%;">
      <tr><td style="padding:0;font-size:0;line-height:0;">
        <a href="${watchUrl}" target="_blank" style="display:block;text-decoration:none;"${sesAttr({ trackingId: p.thumbnailTrackingId, noTrack: p.thumbnailNoTrack })}>
          <img src="${thumbSrc}" alt="Watch video" width="460" style="display:block;width:100%;max-width:460px;height:auto;border:0;border-radius:8px 8px 0 0;" />
        </a>
      </td></tr>
      <tr><td align="center" bgcolor="#1f2937" style="padding:9px 12px;text-align:center;border-radius:0 0 8px 8px;">
        <a href="${watchUrl}" target="_blank" style="color:#ffffff;font-size:13px;font-weight:700;text-decoration:none;font-family:Arial,Helvetica,sans-serif;"${sesAttr(p)}>&#9658;&nbsp;&nbsp;Watch video</a>
      </td></tr>
    </table>
  </td></tr>
</table>`
}

function renderDivider(p) {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}">
    <div style="border-top:${p.thickness}px ${p.style} ${p.color};font-size:0;line-height:0;">&nbsp;</div>
  </td></tr>
</table>`
}

function renderSpacer(p) {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td height="${p.height}" style="height:${p.height}px;font-size:0;line-height:0;${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}">&nbsp;</td></tr>
</table>`
}

// Social icons in the sent email use real hosted logo images — a genuine remote URL,
// not a data-URI. Data-URI/inline images get stripped by Gmail/Outlook on delivery
// (that was the earlier broken-image bug); a normal remote <img src="https://..."> renders
// like any other image. Most platforms are served from Simple Icons' official CDN
// (cdn.simpleicons.org/<slug>/<hex-color>). LinkedIn is the exception — its icon was
// removed from Simple Icons' dataset after a takedown request from LinkedIn, so that
// slug 404s permanently there; it's served from Icons8's hotlinkable set instead.
const SOCIAL_LOGO_URLS = {
  facebook: 'https://cdn.simpleicons.org/facebook/ffffff',
  twitter: 'https://cdn.simpleicons.org/x/ffffff',
  instagram: 'https://cdn.simpleicons.org/instagram/ffffff',
  youtube: 'https://cdn.simpleicons.org/youtube/ffffff',
  linkedin: 'https://img.icons8.com/ios-filled/100/ffffff/linkedin.png',
}
const SOCIAL_LABELS = { facebook: 'Facebook', twitter: 'X', instagram: 'Instagram', linkedin: 'LinkedIn', youtube: 'YouTube' }

function renderSocial(p, tagPlan) {
  const iconSize = Number(p.iconSize) || 36
  const gap = Math.floor((Number(p.gap) || 12) / 2)
  const platforms = [
    { key: 'facebook',  url: p.facebook,  enabled: p.facebookEnabled },
    { key: 'twitter',   url: p.twitter,   enabled: p.twitterEnabled },
    { key: 'instagram', url: p.instagram, enabled: p.instagramEnabled },
    { key: 'linkedin',  url: p.linkedin,  enabled: p.linkedinEnabled },
    { key: 'youtube',   url: p.youtube,   enabled: p.youtubeEnabled },
  ].filter(pl => pl.enabled && pl.url)

  const icons = platforms.map(pl => {
    const bg = (p.iconColor || SOCIAL_BRAND_COLORS[pl.key] || '#000000').replace('#', '')
    const glyphSize = Math.round(iconSize * 0.5)
    const logoSrc = SOCIAL_LOGO_URLS[pl.key]

    return `<td style="padding:0 ${gap}px;">
      <a href="${pl.url}" target="_blank" style="display:inline-block;overflow:hidden;width:${iconSize}px;height:${iconSize}px;border-radius:50%;background-color:#${bg};text-align:center;line-height:${iconSize}px;text-decoration:none;"${sesAttr({ trackingId: tagPlan?.[pl.key] })}>
        <img src="${logoSrc}" width="${glyphSize}" height="${glyphSize}" alt="${SOCIAL_LABELS[pl.key] || pl.key}" style="display:inline-block;vertical-align:middle;width:${glyphSize}px;height:${glyphSize}px;border:0;" />
      </a>
    </td>`
  }).join('')

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}text-align:${p.alignment};">
    <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="display:inline-table;"><tr>${icons}</tr></table>
  </td></tr>
</table>`
}

function renderFooter(p) {
  // Older/pre-built footers still carry their unsubscribe link baked into
  // `content` as literal HTML (no unsubscribeUrl field to speak of) — leave
  // those untouched rather than appending a second link.
  const unsubscribe = p.unsubscribeUrl
    ? ` <a href="${p.unsubscribeUrl}" style="color:${p.color};"${sesAttr({ trackingId: p.unsubscribeTrackingId, noTrack: p.unsubscribeNoTrack })}>${escText(p.unsubscribeLabel || 'Unsubscribe')}</a>`
    : ''
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}font-size:${p.fontSize}px;color:${p.color};text-align:${p.alignment};line-height:1.6;">${p.content}${unsubscribe}</td></tr>
</table>`
}

/**
 * Normalisation applied to a Custom HTML block's markup (ET_029).
 *
 * The block is authored once but rendered in two very different documents: the
 * app (where Chakra's global reset has already zeroed h1–h4/p/ul margins) and the
 * email (where the client's UA defaults apply, which are large and differ per
 * client). Left alone the same snippet came out with different heading sizes,
 * different gaps and different list indents in the editor and the preview.
 *
 * One declaration set, exported as data so BOTH consumers use it verbatim —
 * htmlExport injects it as a <style> block, CanvasBlock feeds it to Chakra's `sx`.
 * Keep them in this shape (selector → declarations) so they cannot drift.
 */
export const HTML_BLOCK_CLASS = 'xn-html-block'
export const HTML_BLOCK_RESET = {
  'h1, h2, h3, h4, h5, h6': 'margin:0 0 8px; line-height:1.25; font-weight:700;',
  h1: 'font-size:32px;',
  h2: 'font-size:28px;',
  h3: 'font-size:24px;',
  h4: 'font-size:20px;',
  h5: 'font-size:16px;',
  h6: 'font-size:14px;',
  p: 'margin:0 0 10px;',
  'ul, ol': 'margin:0 0 10px; padding-left:22px;',
  ul: 'list-style:disc;',
  ol: 'list-style:decimal;',
  li: 'margin:0 0 4px;',
  img: 'max-width:100%; display:block; border:0;',
  a: 'text-decoration:underline;',
  '> *:last-child': 'margin-bottom:0;',
}

// Same rules as a <style> sheet, each selector scoped under the block class.
export function htmlBlockResetCss() {
  return Object.entries(HTML_BLOCK_RESET)
    .map(([sel, decls]) => sel
      .split(',')
      .map(s => `.${HTML_BLOCK_CLASS} ${s.trim()}`)
      .join(', ') + ` { ${decls} }`)
    .join('\n    ')
}

function renderHtml(p) {
  const reset = `<style>\n    ${htmlBlockResetCss()}\n  </style>`
  const wrap = 'overflow-wrap:break-word;word-wrap:break-word;word-break:normal;'
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${p.backgroundColor ? `background-color:${p.backgroundColor};` : ''}${fontStyle(p)}${wrap}">${reset}<div class="${HTML_BLOCK_CLASS}">${p.content}</div></td></tr>
</table>`
}

function renderColumns(p, socialPlan) {
  const colCount = Number(p.columns) || 2
  const colDefs = p.columnDefs || []
  const colWidth = Math.floor(100 / colCount)

  const cols = Array.from({ length: colCount }, (_, i) => {
    const col = colDefs[i] || {}
    const colBg = col.backgroundColor ? `background-color:${col.backgroundColor};` : ''
    const subBlocksHtml = (col.blocks || []).map(b => renderBlock(b, socialPlan)).join('')
    return `<td class="col-block" width="${colWidth}%" valign="top" style="${colBg}padding:${Math.floor((p.gap || 16) / 2)}px;">${subBlocksHtml}</td>`
  }).join('')

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
  <tr><td style="${pad(p)}${bgStyle(p)}">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
      <tr>${cols}</tr>
    </table>
  </td></tr>
</table>`
}
